import * as echarts from 'echarts';
import { DateTime } from 'luxon';
import React from 'react';

import max from 'lodash/max';
import { findAqiLevel } from '../../shared/aqi';
import { MeasurementSeries } from '../../shared/measurements';
import { AirQualityClassificationType, AqiLevelDto, AqiStandardDto } from '../../types';

interface AQIHistoryChart {
  data: MeasurementSeries;
  times: string[];
  aqiStandard: AqiStandardDto;
  aqiLevel: AqiLevelDto;
  isLoading: boolean;
  chartHeight: number;
  chartMargin?: number;
}

export const AQIHistoryChart: React.FC<AQIHistoryChart> = ({
  data,
  times: _times,
  isLoading,
  aqiStandard,
  aqiLevel,
  chartHeight,
  chartMargin = 10,
}) => {
  const getAqiDescription = React.useCallback(
    (value: number | undefined | null) => {
      return findAqiLevel(aqiStandard, value).description;
    },
    [aqiStandard]
  );

  // Add one hour to display the end times
  const times = React.useMemo(() => _times.map((t) => DateTime.fromISO(t).plus({ hours: 1 }).toISO()), [_times]);

  const yAxisMax = React.useMemo(() => {
    if (aqiStandard.aqiClassType === AirQualityClassificationType.AQC || aqiStandard.aqiStandardId === 'UK-DEFRA') {
      const level = findAqiLevel(aqiStandard, max(data.series));
      return level.rangeEnd;
    }
    return null; // Let ECharts decide the max value
  }, [data.series, aqiStandard]);

  const yAxisInterval = React.useMemo(() => {
    const { aqiLevels, aqiClassType, aqiStandardId } = aqiStandard;
    if (aqiClassType === AirQualityClassificationType.AQC) {
      // Use a fixed interval for AQC.
      // Note: assumes that the range is the same for all levels
      const { rangeStart, rangeEnd } = aqiLevels[0];
      return rangeEnd - rangeStart;
    }
    if (aqiStandardId === 'UK-DEFRA') {
      // So that the chart looks better w/o fractional values
      return 1;
    }
    return null; // Let ECharts decide the interval
  }, [aqiStandard, data.series]);

  const option: echarts.EChartsOption = React.useMemo(() => {
    // This guarantees that the last hour of the chart will always be displayed
    const lastDate = new Date(times.at(-1)!);
    const lastHour = lastDate.getHours();
    const lastHourOffset = (lastHour ?? 0) % 6;
    console.log({ lastHour, lastHourOffset });

    const browserLocale = navigator.language;

    const missingTimes = data.series
      .map((v, i) => [v, i])
      .filter(([v]) => v === null || v === undefined)
      .map(([, i]) => times[i!]);

    return {
      xAxis: {
        type: 'category',
        data: times,
        axisLabel: {
          formatter: (value: string) => {
            const date = DateTime.fromISO(value).setLocale(browserLocale);
            const hour = date.hour;
            if (hour === lastHourOffset) {
              return date.toFormat('LLL dd'); // LLL represents locale's month
            } else if (hour % 6 === lastHourOffset) {
              const isLatest = DateTime.fromISO(value).equals(DateTime.fromJSDate(lastDate));
              return `${isLatest ? 'latest - ' : ''}${date.toFormat('HH:mm')}`;
            }
            return '';
          },
          interval: 0,
          rotate: 45,
          barWidth: '5%', // This will make the bars wider
        },
        axisTick: {
          interval: (_index: number, value: string) => {
            const date = DateTime.fromISO(value);
            const hour = date.hour;
            return hour === lastHourOffset || hour % 6 === lastHourOffset;
          }, // Show ticks only when there's a label
        },
      },
      yAxis: {
        type: 'value',
        max: yAxisMax,
        interval: yAxisInterval,
        axisLabel: {
          formatter(value) {
            if (aqiStandard.aqiClassType === AirQualityClassificationType.AQC) {
              // Don't show the label on 0 or null values
              return value ? getAqiDescription(value) : '';
            }
            return value;
          },
        },
        nameLocation: 'middle',
        nameGap: 30, // distance between the axis name and the axis line
        splitNumber: 3,
      },
      series: [
        {
          data: times.map((_, i) => ({
            value: data.series[i],
            itemStyle: { color: data.colors[i] },
          })),
          markPoint: {
            // Show missing values as a cross
            data: missingTimes.map((time) => ({
              name: 'Missing',
              coord: [time, (yAxisMax ?? max(data.series) ?? 0) * 0.02],
              symbol: 'path://M0.2,0.2 L0.8,0.8 M0.8,0.2 L0.2,0.8', // renders an "X"
              symbolSize: 4,
              symbolKeepAspect: true,
              itemStyle: {
                borderWidth: 1,
                borderColor: '#bbbbbb', // same as ionic 'medium'
              },
            })),
          },
          type: 'bar',
        },
      ],
      tooltip: {
        trigger: 'axis',
        formatter: (params) => {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const param = (params as any)[0];
          const date = new Date(param.axisValue);
          const endDate = DateTime.fromJSDate(date);
          const startDate = endDate.minus({ hours: 1 });
          const timeZone =
            new Intl.DateTimeFormat('en', {
              timeZoneName: 'short',
              timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            })
              .formatToParts(date)
              .find((part) => part.type === 'timeZoneName')?.value || '';

          const concString =
            aqiStandard.aqiClassType === AirQualityClassificationType.AQC || param.data.value === null
              ? ''
              : `${data.seriesLabel}: ${param.data.value}<br/>`;

          const fullFormat = 'yyyy-MM-dd HH:mm';
          const period =
            startDate.hour === 23
              ? `${startDate.toFormat(fullFormat)} to ${endDate.toFormat(fullFormat)}`
              : `${startDate.toFormat(fullFormat)} to ${endDate.toFormat('HH:mm')}`;
          return `${period} ${timeZone}<br/>${concString}${getAqiDescription(param.data.value)}`;
        },
        axisPointer: {
          type: 'shadow',
        },
        textStyle: {
          fontSize: 13,
        },
        position: [-10, '-60%'],
      },
      grid: {
        top: chartMargin,
        right: 0,
        bottom: chartMargin + 45,
        left: 0,
        containLabel: true,
      },
      animation: !isLoading,
    } as echarts.EChartsOption;
  }, [data, times, aqiStandard, getAqiDescription, chartMargin, isLoading]);

  const echartsContainer = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (!echartsContainer.current || !option) {
      return;
    }
    // listen to echarts container resize
    const resizeObserver = new ResizeObserver(() => {
      // The point of this function is to initialize the chart at the point
      // where the container has a defined size.
      if (!echartsContainer.current) {
        return;
      }
      let chart = echarts.getInstanceByDom(echartsContainer.current);
      if (!chart) {
        chart = echarts.init(echartsContainer.current);
      }
      chart.setOption(option);
    });

    // start observing the container
    resizeObserver.observe(echartsContainer.current);

    // stop observing when the component is unmounted
    return () => {
      resizeObserver.disconnect();
    };
  }, [echartsContainer, option]);

  // Show loading when the data is still loading
  React.useEffect(() => {
    if (!echartsContainer.current || !option) {
      return;
    }
    const chart = echarts.getInstanceByDom(echartsContainer.current);
    if (!chart) {
      return;
    }
    if (isLoading) {
      chart.showLoading({
        text: 'Loading',
        maskColor: 'rgba(241, 249, 255, 0.80)',
        lineWidth: 3,
        fontFamily: 'Nunito, sans-serif',
        color: aqiLevel.color,
      });
    } else {
      chart.hideLoading();
    }
  }, [echartsContainer, isLoading, aqiLevel]);

  return <div ref={echartsContainer} style={{ height: chartHeight, padding: 0, margin: 0 }}></div>;
};
